/**
 * GOOGLE FONTS INTEGRATION
 * Loads Noto Sans fonts from Google Fonts CDN for comprehensive Unicode support
 * Supports English + Indic languages (Hindi, Malayalam, Tamil, Bengali, Telugu, etc.)
 */

// Google Fonts URL with all required Noto Sans variants
const FONT_URL = 'https://fonts.googleapis.com/css2?family=' + [
  'Noto+Sans:ital,wght@0,400;0,700;1,400;1,700',
  'Noto+Sans+Devanagari:wght@400;700',  // Hindi, Marathi, Sanskrit
  'Noto+Sans+Malayalam:wght@400;700',   // Malayalam
  'Noto+Sans+Tamil:wght@400;700',       // Tamil
  'Noto+Sans+Bengali:wght@400;700',     // Bengali, Assamese
  'Noto+Sans+Telugu:wght@400;700',      // Telugu
  'Noto+Sans+Kannada:wght@400;700',     // Kannada
  'Noto+Sans+Gujarati:wght@400;700',    // Gujarati
  'Roboto:wght@400;700'                  // Additional fallback
].join('&family=') + '&display=swap';

// Universal font family fallback chain
// Browser automatically selects the appropriate font based on character Unicode range
export const FONT_FAMILY = '"Noto Sans", "Noto Sans Devanagari", "Noto Sans Malayalam", "Noto Sans Tamil", "Noto Sans Bengali", "Noto Sans Telugu", "Noto Sans Kannada", "Noto Sans Gujarati", "Roboto", system-ui, sans-serif';

let fontsLoaded = false;
let loadingPromise = null;

/**
 * Load Google Fonts by injecting stylesheet link
 * @returns {Promise<void>} Resolves when fonts are ready
 */
export const loadGoogleFonts = () => {
  // Return existing promise if already loading
  if (loadingPromise) {
    return loadingPromise;
  }

  // Return immediately if already loaded
  if (fontsLoaded) {
    return Promise.resolve();
  }

  loadingPromise = new Promise((resolve) => {
    // Check if link already exists
    const existingLink = document.querySelector(`link[href="${FONT_URL}"]`);
    if (existingLink) {
      document.fonts.ready.then(() => {
        fontsLoaded = true;
        resolve();
      });
      return;
    }

    // Create and inject Google Fonts link
    const link = document.createElement('link');
    link.href = FONT_URL;
    link.rel = 'stylesheet';
    document.head.appendChild(link);

    // Wait for fonts to be ready
    document.fonts.ready.then(() => {
      fontsLoaded = true;
      console.log('✅ Google Fonts loaded successfully');
      resolve();
    });
  });

  return loadingPromise;
};

/**
 * Detect script/language from text to provide appropriate font hint
 * Returns the detected script name (used for logging/debugging)
 *
 * @param {string} text - Text to analyze
 * @returns {string} Detected script name
 */
export const detectLanguage = (text) => {
  if (!text) return 'latin';

  for (let i = 0; i < text.length; i++) {
    const charCode = text.charCodeAt(i);

    // Unicode ranges for Indic scripts
    if (charCode >= 0x0900 && charCode <= 0x097F) return 'devanagari'; // Hindi
    if (charCode >= 0x0980 && charCode <= 0x09FF) return 'bengali';
    if (charCode >= 0x0A80 && charCode <= 0x0AFF) return 'gujarati';
    if (charCode >= 0x0B00 && charCode <= 0x0B7F) return 'oriya';
    if (charCode >= 0x0B80 && charCode <= 0x0BFF) return 'tamil';
    if (charCode >= 0x0C00 && charCode <= 0x0C7F) return 'telugu';
    if (charCode >= 0x0C80 && charCode <= 0x0CFF) return 'kannada';
    if (charCode >= 0x0D00 && charCode <= 0x0D7F) return 'malayalam';
    if (charCode >= 0x0D80 && charCode <= 0x0DFF) return 'sinhala';

    // Other scripts
    if (charCode >= 0x0E00 && charCode <= 0x0E7F) return 'thai';
    if (charCode >= 0x0600 && charCode <= 0x06FF) return 'arabic';
    if (charCode >= 0x0590 && charCode <= 0x05FF) return 'hebrew';
  }

  return 'latin';
};

/**
 * Get appropriate font family for text (for explicit font selection if needed)
 * In most cases, use FONT_FAMILY constant instead - browser handles fallback automatically
 *
 * @param {string} text - Text to analyze
 * @returns {string} Recommended font family
 */
export const getFontForText = (text) => {
  const script = detectLanguage(text);

  const scriptFontMap = {
    'devanagari': 'Noto Sans Devanagari',
    'malayalam': 'Noto Sans Malayalam',
    'bengali': 'Noto Sans Bengali',
    'tamil': 'Noto Sans Tamil',
    'telugu': 'Noto Sans Telugu',
    'kannada': 'Noto Sans Kannada',
    'gujarati': 'Noto Sans Gujarati',
    'latin': 'Noto Sans'
  };

  return scriptFontMap[script] || 'Noto Sans';
};
